/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../dataPointActions/api/DataPointActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], (APIFactory, StringResources, DataPointActionsProviderAPI, ContentActionsProviderAPI) => {

	class ContextualGridAction {

		constructor({ dashboardAPI, content, features }) {
			this.dashboard = dashboardAPI || features && features.API;
			this.features = features;
			if (content) {
				// dataPointAction will pass in the content api
				this.content = content;
				if (features) {
					features.DataPointActions.registerProvider('contextualGridAction', this.getAPI());
				}
			} else {
				features && features.ContentActions.registerProvider('contextualGridAction', this.getAPI());
			}
		}

		/**
		 * dataPoint actions
		 * @param {*} selections
		 * @param {*} options
		 */
		getDataPointActionList(selections, options) {
			const content = this.content;
			const bShowAllOptions = !options.noFilters;
			if (bShowAllOptions && this.dashboard.getMode() === this.dashboard.MODES.EDIT) {
				return this._getContributionList(content, true/*shouldCloseOnActionApplied*/, true/*isTextOnly*/);
			}
			return [];
		}

		_supportAction(idList, content) {
			const enableShowDataOfViz = this._isInteractionEnabled('showData') && this.dashboard.getMode() === this.dashboard.MODES.EDIT;
			if ((idList && idList.length > 1) || !enableShowDataOfViz) {
				//contexualGrid does not support multi-widget Selections
				return false;
			} else {
				const state = content.getFeature('state');
				if (state.getError()) {
					//don't show data when widget is in error state
					return false;
				}
			}
			return true;
		}

		/**
		 * widgetAction interface
		 * @param {*} idList
		 */
		getContentActionList(idList) {
			const contentId = idList[0];
			const content = this.dashboard.getCanvas().getContent(contentId);
			if (this._supportAction(idList, content)) {
				return this._getContributionList(content, false /*shouldCloseOnActionApplied*/);
			} else {
				return [];
			}
		}

		_isSupportedFromVisDefinition(content) {
			const visualization = content.getFeature('Visualization');
			if (!visualization) {
				//todo: the dashboard-feature loader should support to wait content-Visualization feature is ready.
				return false;
			}
			const definition = visualization.getDefinition();
			let supportsDataBehindTheViz = false;
			if (definition.getProperty('isMultilayersWidget') || definition.getProperty('noDataQuery') || this.dashboard.getApplicationName() === 'dashboardTemplate') {
				supportsDataBehindTheViz = false;
			} else {
				supportsDataBehindTheViz = true;
			}
			return supportsDataBehindTheViz;
		}

		/**
		 * pop up data trays if the data tray is not open. If it is open, keep it open
		 * @return Promise
		 */
		_popUpDataTray() {
			let canvasExtensions = this.dashboard.getDashboardCoreSvc('.CanvasExtensions');
			if (canvasExtensions) {
				let splitter = canvasExtensions.splitPaneView.getSplitter();
				let handleIndex = this.dashboard.getApplicationName() === 'story' ? 1 : 0;
				return splitter.toggleItem(handleIndex, true /* always show */, { sender: 'showDataBehindVisAction' } );
			} else {
				return Promise.resolve();
			}
		}

		_getContributionList(content, shouldCloseOnActionApplied, isTextOnly = false) {
			if (this._isSupportedFromVisDefinition(content)) {
				const tooltipId = 'showDataBehindVizLabel';
				const icons = this.features['Icons'] || this.features['Dashboard.Icons'];
				const commonQuerySubject = icons.getIcon('common-querySubject');
				return [{
					name: 'contextualGridAction',
					label: StringResources.get(tooltipId),
					text: isTextOnly ? StringResources.get(tooltipId): undefined,
					icon: commonQuerySubject.id,
					type: 'Button',
					group: 'ShowDataBehindVizAction',
					closeOnActionApplied: shouldCloseOnActionApplied,
					section: ContentActionsProviderAPI.ACTION_SECTION.SELECTION_DATA,
					order: 100,
					actions: {
						apply: this._popUpDataTray.bind(this)
					}
				}];
			}
			return [];
		}

		/**
		 * Check whether a particular interaction is enabled.
		 * Perspective drives this config.
		 *
		 * @param {string} name - interaction name
		 *
		 * @return {boolean} TRUE if interaction is enabled and FALSE if not
		 */
		_isInteractionEnabled(name) {
			const config = this.dashboard.getAppConfig('interactions') || {};
			if (config.hasOwnProperty(name) && [false, 'false'].indexOf(config[name]) !== -1) {
				return false;
			}
			return true;
		}

		getAPI() {
			//todo: we need to implement WidgetAciontsProvider as well once it is ready
			return APIFactory.createAPI(this, [DataPointActionsProviderAPI, ContentActionsProviderAPI]);
		}
	}

	return ContextualGridAction;
});
